import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question:
      "Are there depression treatments that don't involve another medication?",
    answer:
      "Yes. FDA-cleared TMS and SAINT® are non-invasive brain stimulation treatments that do not involve medication. Psychotherapy is also a non-medication treatment. SPRAVATO® (esketamine) is a medication but works through a different mechanism than standard antidepressants. Some patients combine medication with TMS or therapy; others pursue non-medication options.",
  },
  {
    question: "Can TMS replace my antidepressant?",
    answer:
      "TMS is sometimes used alongside medication and sometimes as an alternative, but you should not stop any medication without consulting your prescriber. Abrupt discontinuation can cause withdrawal symptoms and relapse. A psychiatrist can help you decide whether to continue, reduce, or change medication during or after TMS.",
  },
  {
    question: "What is the best non-medication treatment for depression?",
    answer:
      "For mild to moderate depression, psychotherapy (CBT, behavioral activation) has the strongest evidence. For treatment-resistant depression, TMS and SAINT® are FDA-cleared non-medication options. ECT has the highest remission rates for severe depression but requires anesthesia. The best option depends on severity, history, and individual circumstances.",
  },
];

export const pageNoMoreMedication: LearnPageData = {
  slug: "depression-treatment-without-another-medication",
  title: "Depression Treatment Without Another Medication | Neuro Wellness",
  description:
    "Non-medication options for depression: TMS, SAINT®, psychotherapy, and ECT. Learn what works when you don't want to add another medication.",
  canonical: "/learn/depression-treatment-without-another-medication",
  h1: "Depression Treatment Options Without Adding Another Medication",
  breadcrumbLabel: "Depression Treatment Without Another Medication",
  crumbs: baseCrumbs("Depression Treatment Without Another Medication"),
  shortAnswer:
    "Yes, there are depression treatments that do not involve adding another medication. FDA-cleared TMS and SAINT® are non-invasive brain stimulation treatments that use magnetic pulses, not drugs. Psychotherapy is also a non-medication option. Some patients combine medication with these treatments; others pursue non-medication approaches. A psychiatrist can help you decide what is appropriate.",
  keyTakeaways: [
    "TMS and SAINT® are FDA-cleared, non-invasive, and medication-free.",
    "Psychotherapy (CBT, behavioral activation) is a non-medication first-line treatment.",
    "SPRAVATO® is a medication but works through a different mechanism than antidepressants.",
    "Do not stop medication without consulting your prescriber.",
    "The best option depends on severity and treatment history.",
  ],

  sections: [
    {
      heading: "Non-Medication Brain Stimulation",
      body: (
        <>
          {P(
            "TMS uses magnetic pulses to stimulate the left dorsolateral prefrontal cortex. SAINT® is an accelerated, fMRI-guided form of TMS that delivers 50 sessions in 5 days. Both are FDA-cleared for adults with major depressive disorder who have not improved on antidepressants. Neither requires anesthesia, and patients are awake during treatment.",
          )}
        </>
      ),
    },
    {
      heading: "Psychotherapy",
      body: (
        <>
          {P(
            "Cognitive behavioral therapy (CBT), behavioral activation, and interpersonal therapy have strong evidence for depression. Therapy is frequently part of a TMS or SAINT® treatment plan and is often covered separately by insurance. Therapy can be used alongside medication or on its own.",
          )}
        </>
      ),
    },
    {
      heading: "When Medication Is Still Appropriate",
      body: (
        <>
          {P(
            "Some patients benefit from combining medication with TMS or therapy. Stopping medication abruptly can cause withdrawal and relapse. If you want to reduce medication, ask your prescriber to plan a gradual taper. This page is not anti-medication — it is about knowing your full range of options.",
          )}
          {P(
            <>
              <a
                href="/learn/depression-treatment-without-medication"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                See our full guide on treating depression without medication
              </a>
              .
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Blumberger DM, et al. THREE-D trial. Lancet. 2018;391(10131):1683-1692.",
      url: "https://pubmed.ncbi.nlm.nih.gov/29726742/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/depression-treatment-without-another-medication#webpage`,
        name: "Depression Treatment Without Another Medication",
        url: `${SITE_URL}/learn/depression-treatment-without-another-medication`,
        description: "Non-medication options for depression.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/depression-treatment-without-another-medication#faq`,
      ),
      breadcrumbJsonld(
        baseCrumbs("Depression Treatment Without Another Medication"),
      ),
    ],
  },
  relatedLinks: [
    {
      title: "Depression Treatment Without Medication",
      href: "/learn/depression-treatment-without-medication",
      description: "Full guide on non-drug options",
    },
    {
      title: "What Is TMS Therapy?",
      href: "/learn/what-is-tms-therapy-for-depression",
      description: "How TMS works",
    },
    {
      title: "SAINT® TMS",
      href: "/saint",
      description: "5-day accelerated TMS",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate",
      description: "Request a consultation",
    },
  ],
};
